import React, { useContext, useEffect, useState } from 'react'
import { PeFilterContext } from '../../Contexts/PeFilterContext';
import Popup from '../Common/Popup';

/**
 * The `FollowExitStatusFilter` function is a React component that renders a dropdown filter for
 * selecting a follow on funding status option using Popup style.
 * @returns JSX elements
 */
function FollowExitStatusFilter({ FollowExitStatusOptions, setFollowExitStatusOptions, customPathname, isPopupOpen, setIsPopupOpen }) {
    const { FollowExitStatus, refineSearchFilters } = useContext(PeFilterContext);
    const [internalPopupOpen, setInternalPopupOpen] = useState(false);
    
    // Use centralized state if provided, otherwise fall back to internal state
    const popupOpen = isPopupOpen !== undefined ? isPopupOpen : internalPopupOpen;
    const setPopupOpen = setIsPopupOpen !== undefined ? setIsPopupOpen : setInternalPopupOpen;

    /* The `useEffect` hook is used to perform side effects in a React functional component. In this
    case, the `useEffect` hook is used to update the `FollowExitStatusOptions` state based on the
    values of `FollowExitStatus`, `customPathname`, and `refineSearchFilters`. */
    useEffect(() => {
        const industryIds = refineSearchFilters[customPathname]?.followexitStatus?.map(value => value.id) ?? [];
        setFollowExitStatusOptions(FollowExitStatus?.map(industry => ({ ...industry, isChecked: industryIds.includes(industry.id) })))
    }, [FollowExitStatus, customPathname, refineSearchFilters, setFollowExitStatusOptions])


    /**
     * The handleChange function updates the isChecked property of each object in the
     * FollowExitStatusOptions array based on the selected value from the event target.
     * @param e - The parameter `e` is an event object that is passed to the `handleChange` function.
     * It represents the event that triggered the function, such as a change event on an input element.
     */
    const handleChange = (e) => {
        const value = e.target.value
        setFollowExitStatusOptions(FollowExitStatusOptions?.map(type => ({ ...type, isChecked: value === "" ? false : type.id === parseInt(value) })))
    };

    const selected = FollowExitStatusOptions?.filter(option => option.isChecked)?.at(0)

    return (
        <div className="relative w-full md:inline-block">
            <button
                type="button"
                className="h-10 rounded-lg px-4 py-1 text-gray-500 hover:bg-gray-100 text-sm shadow-md border border-gray-300 w-full md:w-[90%] text-left flex items-center justify-between"
                onClick={() => setPopupOpen(!popupOpen)}
            >
                <span className="truncate pr-2">Follow on Funding Status</span>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {selected?.name && (
                        <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
                            {selected.name}
                        </span>
                    )}
                    <span className={`transition-transform duration-200 text-xl font-semibold ${popupOpen ? 'rotate-[270deg]' : 'rotate-90'}`}>
                        ›
                    </span>
                </div>
            </button>

            {popupOpen && (
                <Popup onClose={() => setPopupOpen(false)}>
                    <div className="p-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Follow on Funding Status
                        </label>
                        <select 
                            value={selected?.id ?? ""} 
                            onChange={handleChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#BC8B20]"
                        >
                            <option value="">All</option>
                            {FollowExitStatusOptions?.map((exited, index) => (
                                <option key={exited.id} value={exited.id}>{exited.name}</option>
                            ))}
                        </select>
                    </div>
                </Popup>
            )}
        </div>
    )
}

export default FollowExitStatusFilter